import React, {FC, useState, useEffect} from "react"
import PropTypes from "prop-types"

import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import Pill from "@irati/ui/src/Pill"
import theme from "@irati/ui/src/global/theme"
import OnboardingService, {OnboardingStep} from "@irati/onboarding/services/onboarding"
import authentication from "@irati/auth"
import GenericModal from "@irati/ui/src/GenericModal"
import {H1, Body} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import adminTracking, {adminDashboardTracking} from "@irati-tracking/admin"
import RewardsImage from "@irati/ui/images/globalRewards.png"
import Flex from "@irati/ui/src/Flex"
import Link from "@irati/ui/src/Link"
import History from "@irati/utils/history"

import {NewPillWrapper, RelativeDiv} from "../styled"
import AdminRouterLink from "./AdminRouterLink"

interface Props extends InjectIntl {
  isCollapsed: boolean
}

interface Context {
  apps: {[key: string]: string},
  appPath: string
}

const RewardsAdminRouterLink: FC<Props> = ({isCollapsed, formatMessage}, {apps}: Context) => {
  const {WALLET} = apps
  const [showNewPill, updateShowNewPill] = useState<boolean>(false)
  const [showModal, updateShowModal] = useState<boolean>(false)

  useEffect(() => {
    hasSeenNewRewardsPopup()
  })

  const hasSeenNewRewardsPopup = () => {
    OnboardingService.get(authentication.user().uuid).then((res) => {
      const showNewRewardsPill = !res.userProgress?.find(((item) => item.step === OnboardingStep.HAS_SEEN_NEW_REWARDS_POPUP))
      updateShowNewPill(showNewRewardsPill)
    })
  }

  const handleOpen = () => {
    adminTracking.navigationClickRewardsNew()
    updateShowModal(true)
    OnboardingService.completeStep(authentication.user(), OnboardingStep.HAS_SEEN_NEW_REWARDS_POPUP)
  }

  const handleClose = () => {
    adminDashboardTracking.clickRewardWalletRewardsModal()
    updateShowModal(false)
    History.push("/flexi-reward", WALLET)
  }

  if (!showNewPill) {
    return <RelativeDiv>
      <AdminRouterLink
        id="rewardsPageButton"
        isCollapsed={isCollapsed}
        app={WALLET}
        to="/flexi-reward"
        icon={IconTypes.wallet}
        onClick={adminTracking.navigationClickRewards}
        title="platform.adminNavigation.primary.rewards" />
      {!isCollapsed &&
        <NewPillWrapper>
          <Pill text={formatMessage({id: "platform.offer.label"})} backgroundColor={theme.colors.tertiary.o_400} isWhiteText />
        </NewPillWrapper>
      }
    </RelativeDiv>
  }

  return <>
    <RelativeDiv>
      <AdminRouterLink
        id="rewardsPageButton"
        isCollapsed={isCollapsed}
        to={window.location.pathname.split("/admin")[1]}
        icon={IconTypes.wallet}
        onClick={handleOpen}
        title="platform.adminNavigation.primary.rewards"
        ignoreSelected />
      {!isCollapsed &&
        <NewPillWrapper>
          <Pill text={formatMessage({id: "platform.offer.label"})} backgroundColor={theme.colors.tertiary.o_400} isWhiteText />
        </NewPillWrapper>
      }
    </RelativeDiv>
    <GenericModal
      customWidth={1150}
      show={showModal}
      hideActions
      onHide={handleClose}>
      <Flex alignItems="center" justifyContent="space-between" gap="0 30px">
        <div>
          <H1 marginBottom={3}>
            <FormattedMessage id="platform.adminNavigation.rewards.title" />
          </H1>
          <Flex alignItems="center" marginBottom={2}>
            <Icon icon={IconTypes.globe} marginRight size={30} />
            <Body>
              <FormattedMessage id="platform.adminNavigation.rewards.body1" />
            </Body>
          </Flex>
          <Flex alignItems="center" marginBottom={4}>
            <Icon icon={IconTypes.shoppingCart} marginRight size={30} />
            <Body>
              <FormattedMessage id="platform.adminNavigation.rewards.body2" />
            </Body>
          </Flex>
          <Button primary onClick={handleClose} marginRight={1.5}>
            <FormattedMessage id="platform.adminNavigation.rewards.button1" />
          </Button>
          <Flex inline alignItems="center">
            <Link
              onClick={adminDashboardTracking.clickRewardsVideoRewardsModal}
              href={formatMessage({id: "platform.adminNavigation.rewards.button2.link"})}
              target="_blank">
              <FormattedMessage id="platform.adminNavigation.rewards.button2.text" />
            </Link>
            <Icon icon={IconTypes.export} marginLeft />
          </Flex>
        </div>
        <img src={RewardsImage} alt="Global rewards popup" />
      </Flex>
    </GenericModal>
  </>
}

RewardsAdminRouterLink.contextTypes = {
  apps: PropTypes.object.isRequired,
  appPath: PropTypes.string.isRequired
}

export default injectIntl(RewardsAdminRouterLink)
